import { HttpStatusCode } from 'axios';
import { BaseEntity } from './remoteApi/EntityApi';
import { ErrorCode } from './configs/errorCode';
import { DeepKeys } from '@tanstack/react-table';
import { UUID } from 'crypto';
import {
	FormAnswerMap,
	FormDetails,
	QuantityPerDimensionForm,
	ShippingData,
	UserData,
} from './ui/productTypeForm/ProductTypeForm';

export interface StringSignature<TData = any> {
	[key: string]: TData;
}

export type SortOrder = 'asc' | 'desc';

export interface Sort<TData extends BaseEntity = any> {
	key: DeepKeys<TData> | null;
	order: SortOrder;
}

export interface ApiQueryOptions<TData extends BaseEntity> {
	page: number;
	limit: number;
	search: string;
	sortBy: Sort<TData>;
}

export type CustomFilters = Record<string, unknown>;

export interface ApiPaginatedResponse<TData extends {} = {}> {
	data: TData[];
	meta: {
		currentPage: number;
		itemsPerPage: number;
		totalItems: number;
		totalPages: number;
	};
}

export interface ApiFormattedResponseData<TData extends {} = {}> {
	data: TData[];
	totalPages: number;
}

export interface Attachment {
	id: UUID;
	path: string;
	name: string;
	size: number;
	mimetype: string;
	width: number;
	height: number;
}

export interface Relation {
	answerId: UUID;
	present: boolean;
}

export type ApiErrorMessage = ErrorCode | string | (ErrorCode | string)[];

export interface ApiError {
	error: string;
	message: ApiErrorMessage;
	status: HttpStatusCode;
}

export interface AppError {
	status: HttpStatusCode;
	stack: (ErrorCode | string)[];
	message: string;
}

export enum Gender {
	MALE,
	FEMALE,
	OTHER,
}

export enum SessionStorageKey {
	SAVED_FORM_STATE = 'saved_form_state',
}

export type SavedFormState = {
	productType: UUID;
	quantityPerDimension: QuantityPerDimensionForm;
	userDataForm: UserData;
	shippingDataForm: ShippingData;
	formDetails: Omit<FormDetails, 'orderDate'> & {
		orderDate: string;
	};
	answersMap: [UUID, FormAnswerMap][];
};

export type FormFieldConfig<T extends Record<string, string>> = {
	[K in keyof T]: {
		required?: boolean;
		label?: string;
		validations?: Array<{
			validate: (value: string, values: T) => boolean | Promise<boolean>;
			message: string;
		}>;
	};
};

export type FormHandlerConfig<T extends Record<string, string>> = {
	fields: FormFieldConfig<T>;
	onSubmit: (values: T) => Promise<any>;
	successMessage: string;
};

export type FormState<T extends Record<string, string>> = {
	values: Partial<T>;
	message?: string;
	error?: string;
};